import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase/client'
import { deepSelect, toJson } from '@/lib/supabase/typing'
import { useAuthStore, useToast } from '@/stores'
import { invalidateVendorEngagements } from '@/hooks/vendor-engagement-cache'
import type { WorkOrder, WorkOrderWithDetails, WorkOrderStatus, WorkOrderType } from '@/types'

// Query keys
const WORK_ORDERS_KEY = 'work-orders'


// Filter options
export interface WorkOrderFilters {
  status?: WorkOrderStatus | WorkOrderStatus[]
  type?: WorkOrderType | WorkOrderType[]
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  assignedTo?: string
  equipmentId?: string
  locationId?: string
  search?: string
  /**
   * Filter to WOs that have at least one task with the given task-level
   * estimate_status (wo_estimate_status enum). Single value or array.
   */
  estimateStatus?: string | string[]
  /** Optional hard cap on rows returned; defaults to 500 for list views */
  limit?: number
  /**
   * Scope to a single Monday board (e.g. the active CFA 2026 board, or an
   * archive year like 2021). When set, query adds .eq('monday_board_id', boardId)
   * — archive WOs do not bleed into the active list, and vice versa. When unset,
   * defaults to excluding archive (read_only=true) rows so the existing /work-orders
   * page keeps its current "all active WOs" semantics without a code change.
   * See docs/MONDAY_ARCHIVE_BOARDS_PLAN.md
   */
  boardId?: string
  /**
   * Show every archive (read_only=true) WO across all 5 archive boards.
   * Used by the consolidated /work-orders/archive page so users browse 2021-
   * 2025 history in one place instead of bouncing between per-year pages.
   * Mutually exclusive with boardId — boardId wins if both are set.
   */
  archiveOnly?: boolean
}

// Defensive cap to keep the list query bounded and avoid pulling huge result
// sets that blow the PostgREST pool. Raise the cap explicitly via filters.limit.
const DEFAULT_WO_LIST_LIMIT = 500

// Fetch work orders with filters via the flat SECURITY DEFINER RPC.
//
// Why the RPC: the equivalent PostgREST select did
//   work_orders → equipment_instances → equipment_models, locations, users, vendors
// in one go (3 levels deep). RLS re-evaluated at every join boundary, and with
// ~360 active rows that was the dominant p95 latency on this page. The RPC
// runs as SECURITY DEFINER with org-scope enforced once at the top, builds
// the joined sub-objects directly via jsonb_build_object, and returns the
// same shape the existing components already read. No component change needed.
export function useWorkOrders(filters: WorkOrderFilters = {}) {
  const { user } = useAuthStore()
  const organizationId = user?.organization_id

  return useQuery({
    queryKey: [WORK_ORDERS_KEY, organizationId, filters],
    queryFn: async () => {
      if (!organizationId) return []

      // Translate front-end filter shape to the RPC's snake_case JSONB.
      // Only set keys we actually want to filter on — the RPC treats missing
      // keys as "no filter".
      const p: Record<string, unknown> = {
        limit: filters.limit ?? DEFAULT_WO_LIST_LIMIT,
      }
      if (filters.status)         p.status          = filters.status
      if (filters.type)           p.type            = filters.type
      if (filters.priority)       p.priority        = filters.priority
      if (filters.assignedTo)     p.assigned_to     = filters.assignedTo
      if (filters.equipmentId)    p.equipment_id    = filters.equipmentId
      if (filters.locationId)     p.location_id     = filters.locationId
      if (filters.search)         p.search          = filters.search
      if (filters.estimateStatus) p.estimate_status = filters.estimateStatus

      // Board scope precedence: explicit board_id > archive_only > active default
      if (filters.boardId)             p.board_id     = filters.boardId
      else if (filters.archiveOnly)    p.archive_only = true

      const { data, error } = await supabase.rpc('get_work_orders_list', {
        p_filters: toJson(p),
      })
      if (error) throw error

      // The RPC returns SETOF jsonb (one object per row). Supabase JS hands
      // it back as an array of plain objects already keyed exactly like the
      // old nested PostgREST shape.
      return (data ?? []) as unknown as WorkOrderWithDetails[]
    },
    enabled: !!organizationId,
    // useOrgRealtimeChannel invalidates this on any WO change. 60s of
    // staleness covers remount churn (drawer open/close, navigate back, etc).
    staleTime: 60_000,
  })
}

// Fetch soft-deleted work orders — only when user toggles the deleted panel
export function useDeletedWorkOrders(enabled = false) {
  const { user } = useAuthStore()
  const organizationId = user?.organization_id

  return useQuery({
    queryKey: ['work-orders-deleted', organizationId],
    queryFn: async () => {
      if (!organizationId) return []

      const { data, error } = await deepSelect('work_orders')
        .select(`
          *,
          equipment_instances (
            id,
            serial_number,
            condition,
            equipment_models (
              id,
              manufacturer,
              model_number,
              category
            )
          ),
          locations (
            id,
            name,
            city,
            state
          ),
          users:users!work_orders_assigned_to_fkey (
            id,
            full_name,
            email
          )
        `)
        .eq('organization_id', organizationId)
        .not('deleted_at', 'is', null)
        .order('deleted_at', { ascending: false })

      if (error) throw error
      return data as unknown as WorkOrderWithDetails[]
    },
    enabled: !!organizationId && enabled,
  })
}

// Fetch single work order by ID via SECURITY DEFINER RPC.
// Replaces a 3-level nested PostgREST select that was blowing the 8s timeout
// under load (RLS re-expansion per join). RPC returns flat JSON in <50ms cold.
export function useWorkOrder(id: string | undefined) {
  const { user } = useAuthStore()
  const organizationId = user?.organization_id

  return useQuery({
    queryKey: [WORK_ORDERS_KEY, id],
    queryFn: async () => {
      if (!id || !organizationId) return null

      const { data, error } = await supabase.rpc('get_admin_work_order_detail', {
        p_wo_id: id,
      })
      if (error) throw error
      if (!data) return null

      const r = data as {
        work_order: WorkOrder
        location: Record<string, unknown> | null
        equipment: { model?: Record<string, unknown> | null } & Record<string, unknown> | null
        assigned_user: Record<string, unknown> | null
        vendor: Record<string, unknown> | null
        organization: Record<string, unknown> | null
        maintenance_schedule: Record<string, unknown> | null
        parts: Array<{ part?: Record<string, unknown> | null } & Record<string, unknown>>
        sub_tasks: Array<Record<string, unknown>>
      }

      // Shim RPC payload back into the WorkOrderWithDetails shape consumers expect.
      const shimmed = {
        ...r.work_order,
        equipment_instances: r.equipment
          ? { ...r.equipment, equipment_models: r.equipment.model ?? null }
          : null,
        locations: r.location,
        users: r.assigned_user,
        vendors: r.vendor,
        organizations: r.organization,
        maintenance_schedules: r.maintenance_schedule,
        work_order_parts: (r.parts ?? []).map((p) => ({
          ...p,
          parts_catalog: p.part ?? null,
        })),
        sub_tasks: r.sub_tasks ?? [],
      }
      return shimmed as unknown as WorkOrderWithDetails
    },
    enabled: !!id && !!organizationId,
  })
}

// Create work order input
export interface CreateWorkOrderInput {
  // Both nullable in the database — a WO can be raised without either.
  equipment_id?: string | null
  location_id?: string | null
  title: string
  description?: string
  type?: WorkOrderType
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  assigned_to?: string
  vendor_id?: string
  scheduled_start?: string
  scheduled_end?: string
  service_ticket_id?: string
  schedule_id?: string
  client_id?: string
  customer_wo_number?: string
}

// Generate next work order number using atomic DB function
async function generateWorkOrderNumber(organizationId: string): Promise<string> {
  const { data, error } = await supabase.rpc('generate_wo_number', { p_org_id: organizationId })
  if (error) throw error
  return data as string
}

// Create work order mutation
export function useCreateWorkOrder() {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()

  return useMutation({
    mutationFn: async (input: CreateWorkOrderInput) => {
      if (!user?.organization_id) throw new Error('No organization')

      const workOrderNumber = await generateWorkOrderNumber(user.organization_id)

      const { data, error } = await supabase
        .from('work_orders')
        .insert({
          organization_id: user.organization_id,
          work_order_number: workOrderNumber,
          equipment_id: input.equipment_id ?? null,
          location_id: input.location_id ?? null,
          title: input.title,
          description: input.description,
          type: input.type || 'corrective',
          priority: input.priority || 'medium',
          status: 'pending',
          assigned_to: input.assigned_to,
          vendor_id: input.vendor_id,
          scheduled_start: input.scheduled_start,
          scheduled_end: input.scheduled_end,
          service_ticket_id: input.service_ticket_id,
          schedule_id: input.schedule_id,
          client_id: input.client_id,
          customer_wo_number: input.customer_wo_number,
          parts_cost: 0,
          labor_cost: 0,
        })
        .select()
        .single()

      if (error) throw error
      return data as WorkOrder
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [WORK_ORDERS_KEY], refetchType: 'active' })
    },
  })
}

// Update work order input
export interface UpdateWorkOrderInput {
  id: string
  title?: string
  description?: string
  status?: WorkOrderStatus
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  assigned_to?: string | null
  vendor_id?: string | null
  location_id?: string | null
  equipment_id?: string | null
  scheduled_start?: string | null
  scheduled_end?: string | null
  actual_start?: string | null
  actual_end?: string | null
  resolution?: string
  technician_notes?: string
  labor_hours?: number
}

// Update work order mutation
export function useUpdateWorkOrder() {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()

  return useMutation({
    mutationFn: async (input: UpdateWorkOrderInput) => {
      if (!user?.organization_id) throw new Error('No organization')

      const { id, ...updates } = input

      const { data, error } = await supabase
        .from('work_orders')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('organization_id', user.organization_id)
        .select()
        .single()

      if (error) throw error
      return data as WorkOrder
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [WORK_ORDERS_KEY], refetchType: 'active' })
      queryClient.invalidateQueries({ queryKey: [WORK_ORDERS_KEY, data.id] })
      // Whole-WO vendor assignment changed → refresh the engagement explorer.
      if (variables.vendor_id !== undefined) invalidateVendorEngagements(queryClient)
    },
  })
}

// Update work order status (quick action)
export function useUpdateWorkOrderStatus() {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const toast = useToast()

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: WorkOrderStatus }) => {
      if (!user?.organization_id) throw new Error('No organization')

      const updates: Partial<WorkOrder> = {
        status,
        updated_at: new Date().toISOString(),
      }

      // Set timestamps based on status
      if (status === 'in_progress') {
        updates.actual_start = new Date().toISOString()
      } else if (status === 'completed') {
        updates.actual_end = new Date().toISOString()
      }

      const { data, error } = await supabase
        .from('work_orders')
        .update(updates)
        .eq('id', id)
        .eq('organization_id', user.organization_id)
        .select()
        .single()

      if (error) throw error
      return data as WorkOrder
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [WORK_ORDERS_KEY], refetchType: 'active' })
      queryClient.invalidateQueries({ queryKey: [WORK_ORDERS_KEY, data.id] })
    },
    onError: (err: Error & { code?: string }) => {
      // PGRST116 = update matched no rows (RLS filtered it), not a server fault.
      toast.error(
        'Status not saved',
        err.code === 'PGRST116' ? 'You do not have permission to edit this work order.' : err.message,
      )
    },
  })
}

// Delete work order mutation
export function useDeleteWorkOrder() {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()

  return useMutation({
    mutationFn: async (id: string) => {
      if (!user?.organization_id) throw new Error('No organization')

      // Soft delete â set deleted_at instead of hard deleting
      const { error } = await supabase
        .from('work_orders')
        .update({ deleted_at: new Date().toISOString(), deleted_by: user.id } as any)
        .eq('id', id)
        .eq('organization_id', user.organization_id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [WORK_ORDERS_KEY], refetchType: 'active' })
    },
  })
}

// Fetch WO stage config (Monday board groups)
export interface WoStage {
  id: string
  stage_key: string
  label: string
  sort_order: number
  color: string | null
  is_active: boolean
}

export interface WorkOrderStageStats {
  open: number
  completed: number
  urgent: number
  total: number
}

/**
 * Board summary counts (Open / Completed / Urgent), computed SERVER-SIDE over
 * the full table so they stay exact even when the board exceeds the list load
 * limit. Counts by board STAGE (not the unmaintained wo.status field). Mirrors
 * the board-scope precedence of useWorkOrders: board_id > archive_only > active.
 */
export function useWorkOrderStageStats(filters: WorkOrderFilters = {}) {
  const { user } = useAuthStore()
  const organizationId = user?.organization_id

  return useQuery({
    queryKey: ['wo-stage-stats', organizationId, filters.boardId ?? null, filters.archiveOnly ?? false],
    queryFn: async (): Promise<WorkOrderStageStats> => {
      const empty = { open: 0, completed: 0, urgent: 0, total: 0 }
      if (!organizationId) return empty
      const p: Record<string, unknown> = {}
      if (filters.boardId) p.board_id = filters.boardId
      else if (filters.archiveOnly) p.archive_only = true
      const { data, error } = await supabase.rpc('get_work_orders_stage_stats', { p_filters: toJson(p) })
      if (error) throw error
      return { ...empty, ...(data as Partial<WorkOrderStageStats> | null) }
    },
    enabled: !!organizationId,
    staleTime: 30_000,
  })
}

export function useWoStages(boardId?: string) {
  const { user } = useAuthStore()
  const organizationId = user?.organization_id

  return useQuery({
    queryKey: ['wo-stages', organizationId, boardId ?? null],
    queryFn: async () => {
      if (!organizationId) return []
      let q = supabase
        .from('wo_stage_config')
        .select('id, stage_key, label, sort_order, color, is_active, monday_board_id')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('sort_order')
      if (boardId) {
        // Explicit board → just that board's stages.
        q = q.eq('monday_board_id', boardId)
      } else {
        // Default (active /work-orders page) → exclude any stages that belong
        // to an archive board, so archive groups like "Pre-Process" / "Invoiced
        // AND Completed" don't bleed into the active stage tabs.
        const { data: archiveBoards } = await supabase
          .from('monday_board_registry')
          .select('monday_board_id')
          .eq('organization_id', organizationId)
          .eq('kind', 'archive')
        const archiveIds = (archiveBoards ?? [])
          .map((b) => b.monday_board_id)
          .filter((id): id is string => !!id)
        if (archiveIds.length > 0) {
          q = q.not('monday_board_id', 'in', `(${archiveIds.map((id) => `"${id}"`).join(',')})`)
        }
      }
      const { data, error } = await q
      if (error) throw error
      return data as WoStage[]
    },
    enabled: !!organizationId,
  })
}

// ---------------------------------------------------------------------------
// Monday board registry — drives the archives sidebar + archive route resolution.
// See docs/MONDAY_ARCHIVE_BOARDS_PLAN.md
// ---------------------------------------------------------------------------

export interface MondayBoardRegistryRow {
  id: string
  organization_id: string
  monday_board_id: string
  monday_subitem_board_id: string | null
  display_name: string
  slug: string
  kind: 'active' | 'archive'
  read_only: boolean
  sync_enabled: boolean
  sync_frequency_hours: number
  sort_order: number
  last_synced_at: string | null
  last_sync_status: string | null
}

/**
 * All Monday boards configured for the user's org, in sort_order.
 * Active board first, then archives newest → oldest.
 */
export function useMondayBoards() {
  const { user } = useAuthStore()
  const organizationId = user?.organization_id

  return useQuery({
    queryKey: ['monday-board-registry', organizationId],
    queryFn: async () => {
      if (!organizationId) return []
      const { data, error } = await supabase
        .from('monday_board_registry')
        .select(`id, organization_id, monday_board_id, monday_subitem_board_id,
                 display_name, slug, kind, read_only, sync_enabled,
                 sync_frequency_hours, sort_order, last_synced_at, last_sync_status`)
        .eq('organization_id', organizationId)
        .order('sort_order')
      if (error) throw error
      return (data ?? []) as MondayBoardRegistryRow[]
    },
    enabled: !!organizationId,
    // Registry rarely changes; cache aggressively to avoid hammering it from
    // the sidebar on every page load.
    staleTime: 5 * 60 * 1000,
  })
}

/** Just the archive boards, in sidebar sort order. */
export function useArchiveBoards() {
  const all = useMondayBoards()
  return {
    ...all,
    data: all.data?.filter((b) => b.kind === 'archive') ?? [],
  }
}

/** The active board row (there should be exactly one per org). */
export function useActiveBoard() {
  const all = useMondayBoards()
  return {
    ...all,
    data: all.data?.find((b) => b.kind === 'active') ?? null,
  }
}

// Move WO to a different stage. Writes locally first, then mirrors the move
// to Monday (move_item_to_group) via monday-push-group. The push is
// fire-and-forget: it no-ops for orgs without Monday and WOs never synced.
export function useUpdateWorkOrderStage() {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()

  return useMutation({
    mutationFn: async ({ id, stage, monday_group_id }: { id: string; stage: string; monday_group_id: string }) => {
      if (!user?.organization_id) throw new Error('No organization')
      const { data, error } = await supabase
        .from('work_orders')
        .update({ stage, monday_group_id, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('organization_id', user.organization_id)
        .select()
        .single()
      if (error) throw error
      return data as WorkOrder
    },
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: [WORK_ORDERS_KEY], refetchType: 'active' })
      if (user?.organization_id && data.monday_item_id) {
        supabase.functions
          .invoke('monday-push-group', {
            body: {
              organization_id: user.organization_id,
              work_order_id: vars.id,
              monday_group_id: vars.monday_group_id,
            },
          })
          .then(({ data: res, error: fnErr }) => {
            const msg = fnErr?.message ?? (res as any)?.error
            if (msg) console.warn('monday-push-group failed', msg)
          })
          .catch((e) => console.warn('monday-push-group failed', e))
      }
    },
  })
}

// ---------------------------------------------------------------------------
// Customer-bid-sent flag — org-visible signal that a WO's bid has been sent
// to the customer. Stamping it also auto-completes any open "Send bid..."
// user_todos linked to the WO so they stop nagging teammates.
// ---------------------------------------------------------------------------

export function useMarkCustomerBidSent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (woId: string) => {
      const { data, error } = await supabase.rpc('mark_customer_bid_sent', { p_wo_id: woId })
      if (error) throw error
      return data as { wo_id: string; completed_todos: number }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [WORK_ORDERS_KEY], refetchType: 'active' })
      qc.invalidateQueries({ queryKey: ['my-todos'], refetchType: 'active' })
    },
  })
}

export function useUnmarkCustomerBidSent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (woId: string) => {
      const { error } = await supabase.rpc('unmark_customer_bid_sent', { p_wo_id: woId })
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [WORK_ORDERS_KEY], refetchType: 'active' })
    },
  })
}

// Fetch cross-org vendor-assigned work orders
// Returns WOs from other orgs where user's org is linked as a vendor (via task_vendor_bids)
export function useVendorAssignedWorkOrders() {
  const { user } = useAuthStore()
  const organizationId = user?.organization_id

  return useQuery({
    queryKey: [WORK_ORDERS_KEY, 'vendor-assigned', organizationId],
    queryFn: async () => {
      if (!organizationId) return []

      // Query bids assigned to vendors linked to user's org (v2 bidding system)
      const { data: bids, error: bError } = await supabase
        .from('task_vendor_bids')
        .select(`
          work_order_task_id,
          vendors!inner ( id, name, linked_org_id ),
          work_order_tasks!inner ( work_order_id )
        `)
        .eq('vendors.linked_org_id', organizationId)
        .is('archived_at', null)

      if (bError) throw bError
      if (!bids || bids.length === 0) return []

      // Get unique WO IDs
      const woIds = [...new Set((bids as any[]).map((b: any) => b.work_order_tasks?.work_order_id as string | undefined).filter((v): v is string => !!v))]

      const { data, error } = await deepSelect('work_orders')
        .select(`
          id,
          work_order_number,
          title,
          status,
          priority,
          type,
          created_at,
          updated_at,
          scheduled_start,
          scheduled_end,
          organization_id,
          locations (
            id,
            name,
            city,
            state
          ),
          vendors (
            id,
            name,
            phone
          ),
          organizations (
            id,
            name,
            logo_url
          )
        `)
        .in('id', woIds)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as unknown as WorkOrderWithDetails[]
    },
    enabled: !!organizationId,
  })
}

// Work order stats for dashboard
export function useWorkOrderStats() {
  const { user } = useAuthStore()
  const organizationId = user?.organization_id

  return useQuery({
    queryKey: [WORK_ORDERS_KEY, 'stats', organizationId],
    queryFn: async () => {
      if (!organizationId) return null

      const stats = {
        total: 0,
        byStatus: {
          pending: 0,
          scheduled: 0,
          in_progress: 0,
          on_hold: 0,
          completed: 0,
          cancelled: 0,
        },
        byPriority: {
          low: 0,
          medium: 0,
          high: 0,
          urgent: 0,
        },
        byType: {
          preventive: 0,
          corrective: 0,
          emergency: 0,
          inspection: 0,
        },
        openCount: 0,
      }

      const { data, error } = await supabase
        .from('work_orders')
        .select('status, priority, type')
        .eq('organization_id', organizationId)
        .is('deleted_at', null)

      if (error) throw error

      stats.total = data.length
      data.forEach((wo) => {
        stats.byStatus[wo.status as keyof typeof stats.byStatus]++
        stats.byPriority[wo.priority as keyof typeof stats.byPriority]++
        stats.byType[wo.type as keyof typeof stats.byType]++

        if (!['completed', 'cancelled'].includes(wo.status)) {
          stats.openCount++
        }
      })

      return stats
    },
    enabled: !!organizationId,
  })
}
